import {
  Box,
  Grid,
  Typography,
  useTheme,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import MonitorStatus from '../../components/Animate/monitorStatus'
import WorldMap from '../../components/worldMap/worldMap'
import api from '../../api/axiosInstance'
import {
  AccessTime,
  CheckCircle,
  Error,
  Schedule,
  Person,
  Build,
  Monitor,
  Pause,
  Edit,
  Delete,
  PlayArrow,
  TrendingUp,
  Speed,
  Public,
  ContentCopy,
} from '@mui/icons-material'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { QuestionMark } from 'tabler-icons-react'
import { styled } from '@mui/material/styles'
import ResponseTimeLineChart from '../../components/reportTable/lineChart'
import Swal from 'sweetalert2'

// Styled Components
const GlowCard = styled(Card)(({ theme, active }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(
    theme.palette.background.paper,
    0.6
  )} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${active ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: active
      ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
      : 'transparent',
  },
}))

const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
    theme.palette.primary.main,
    0.05
  )} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'scale(1.02)',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}))

export default function ModernMonitorDetail() {
  const navigate = useNavigate()
  const params = useParams()
  const theme = useTheme()
  const [monitor, setMonitor] = useState(null)
  const [selectOtherMonitor, setSelectOtherMonitor] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (params.id) {
      getMonitorById(params.id)
    }
  }, [params.id])

  const getMonitorById = async (monitorId) => {
    try {
      setLoading(true)
      const [monitorRes, monitorsRes] = await Promise.all([
        api.get(`monitors/${monitorId}`),
        api.get(`monitors/ids-names`)
      ])

      setMonitor(monitorRes.data)
      setSelectOtherMonitor(monitorsRes.data)
      setSelectedOption({
        id: monitorRes.data.id,
        name: monitorRes.data.name,
        monitorType: monitorRes.data.monitorType,
      })
    } catch (error) {
      console.error('Error fetching monitor:', error)
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Monitor bilgileri yüklenemedi.',
        background: alpha(theme.palette.background.paper, 0.95),
        color: '#fff',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (action, actionFn) => {
    try {
      await actionFn()
    } catch (error) {
      console.error(`Error in ${action}:`, error)
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'İşlem başarısız oldu.',
        background: alpha(theme.palette.background.paper, 0.95),
        color: '#fff',
      })
    }
  }

  const handleDeleteButton = () => {
    Swal.fire({
      title: 'Silmek istediğinizden emin misiniz?',
      icon: 'warning',
      html: `
        <div style="color: #fff; background: ${alpha(
          theme.palette.background.paper,
          0.9
        )}; padding: 20px; border-radius: 8px;">
          <p style="color: ${theme.palette.error.main}; font-weight: bold;">İzleme kalıcı olarak silinecek!</p>
        </div>
      `,
      background: alpha(theme.palette.background.paper, 0.95),
      color: '#fff',
      showCancelButton: true,
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'İptal',
      confirmButtonColor: theme.palette.error.main,
      cancelButtonColor: theme.palette.primary.main,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await api.delete(`monitors/${monitor.id}`)
        Swal.fire({
          icon: 'success',
          title: 'Silindi!',
          text: 'İzleme başarıyla silindi.',
          background: alpha(theme.palette.background.paper, 0.95),
          color: '#fff',
        })
        navigate('/user/monitors')
      }
    })
  }

  const handlePlayPause = async () => {
    const endpoint = monitor.isActiveByOwner ? 'pause' : 'play'
    const action = monitor.isActiveByOwner ? 'durduruldu' : 'başlatıldı'

    try {
      await api.patch(`monitors/${monitor.id}/${endpoint}`, {})
      Swal.fire({
        icon: 'success',
        title: `İzleme ${action}`,
        background: alpha(theme.palette.background.paper, 0.95),
        color: '#fff',
        timer: 2000,
      })
      setMonitor({
        ...monitor,
        isActiveByOwner: !monitor.isActiveByOwner,
        status: 'uncertain',
      })
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'İşlem başarısız.',
        background: alpha(theme.palette.background.paper, 0.95),
        color: '#fff',
      })
    }
  }

  const calculateStats = () => {
    if (!monitor?.logs) return {}
    const logs = [...monitor.logs].sort((a, b) => a.id - b.id)
    const totalLogs = logs.length
    const upLogs = logs.filter((log) => log.status === 'up').length
    const downLogs = logs.filter((log) => log.status === 'down').length
    const uptime = totalLogs > 0 ? ((upLogs / totalLogs) * 100).toFixed(2) : 0
    const lastLogs = logs.length > 250 ? logs.slice(-250) : logs
    const avgResponseTime =
      lastLogs.reduce((sum, log) => sum + log.responseTime, 0) / lastLogs.length
    const maxResponseTime = Math.max(...lastLogs.map((log) => log.responseTime))
    const minResponseTime = Math.min(...lastLogs.map((log) => log.responseTime))

    return {
      totalLogs,
      upLogs,
      downLogs,
      uptime,
      lastLogs,
      avgResponseTime: avgResponseTime.toFixed(2),
      maxResponseTime,
      minResponseTime,
    }
  }

  const stats = calculateStats()

  const getStatusConfig = (status) => {
    const configs = {
      up: {
        color: theme.palette.success.main,
        icon: CheckCircle,
        label: 'Çalışıyor',
      },
      down: {
        color: theme.palette.error.main,
        icon: Error,
        label: 'Çalışmıyor',
      },
      uncertain: {
        color: theme.palette.warning.main,
        icon: QuestionMark,
        label: 'Belirsiz',
      },
      maintanance: {
        color: theme.palette.info.main,
        icon: Build,
        label: 'Bakım',
      },
    }
    return configs[status] || configs.uncertain
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    Swal.fire({
      icon: 'success',
      title: 'Kopyalandı!',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
      background: alpha(theme.palette.background.paper, 0.95),
      color: '#fff',
    })
  }

  if (loading || !monitor) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            component={motion.div}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            sx={{
              width: 60,
              height: 60,
              border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderTopColor: theme.palette.primary.main,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
            }}
          />
          <Typography sx={{ color: alpha('#fff', 0.7) }}>Yükleniyor...</Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <MonitorStatus
                sx={{
                  width: 40,
                  height: 40,
                  animeWidth: 24,
                  animeHeight: 24,
                }}
                status={monitor.status}
                iconSize={20}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  value={selectedOption}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setSelectedOption(newValue)
                      navigate(`/user/monitors/${newValue.id}/detail`)
                    }
                  }}
                  options={selectOtherMonitor.filter((item) => item.id !== selectedOption?.id)}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(10px)',
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                          {option.monitorType}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
                {selectedOption && (
                  <Typography variant="caption" sx={{ ml: 1, color: alpha('#fff', 0.6) }}>
                    {selectedOption.monitorType}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
              <Tooltip title="Edit" arrow>
                <IconButton
                  onClick={() => navigate(`/user/monitors/${monitor.id}/monitor`)}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.secondary.main,
                      0.2
                    )}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.2),
                      borderColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <Edit sx={{ color: theme.palette.secondary.main }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={monitor.isActiveByOwner ? 'Pause' : 'Play'} arrow>
                <IconButton
                  onClick={handlePlayPause}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}, ${alpha(theme.palette.primary.main, 0.1)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.2),
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {monitor.isActiveByOwner ? (
                    <Pause sx={{ color: theme.palette.primary.main }} />
                  ) : (
                    <PlayArrow sx={{ color: theme.palette.primary.main }} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete" arrow>
                <IconButton
                  onClick={handleDeleteButton}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.error.main,
                      0.2
                    )}, ${alpha(theme.palette.error.main, 0.1)})`,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    '&:hover': {
                      background: alpha(theme.palette.error.main, 0.2),
                      borderColor: theme.palette.error.main,
                    },
                  }}
                >
                  <Delete sx={{ color: theme.palette.error.main }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 4, borderColor: alpha(theme.palette.primary.main, 0.2) }} />

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            label: 'Uptime',
            value: `${stats.uptime}%`,
            icon: TrendingUp,
            color: theme.palette.success.main,
            progress: stats.uptime,
          },
          {
            label: 'Avg Response',
            value: `${stats.avgResponseTime}ms`,
            icon: Speed,
            color: theme.palette.info.main,
          },
          {
            label: 'Total Checks',
            value: stats.totalLogs,
            icon: CheckCircle,
            color: theme.palette.primary.main,
          },
          {
            label: 'Failed',
            value: stats.downLogs,
            icon: Error,
            color: theme.palette.error.main,
          },
        ].map((stat, index) => (
          <Grid item xs={6} md={3} key={index}>
            <StatCard
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), textTransform: 'uppercase' }}>
                  {stat.label}
                </Typography>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    background: alpha(stat.color, 0.1),
                  }}
                >
                  <stat.icon sx={{ fontSize: 18, color: stat.color }} />
                </Avatar>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  color: stat.color,
                  fontWeight: 900,
                  mb: stat.progress ? 1 : 0,
                }}
              >
                {stat.value}
              </Typography>
              {stat.progress && (
                <LinearProgress
                  variant="determinate"
                  value={stat.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    background: alpha(stat.color, 0.1),
                    '& .MuiLinearProgress-bar': {
                      background: `linear-gradient(90deg, ${stat.color}, ${alpha(stat.color, 0.7)})`,
                      borderRadius: 3,
                    },
                  }}
                />
              )}
            </StatCard>
          </Grid>
        ))}
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Monitor Details */}
            <GlowCard active>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Monitor sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Monitor Details
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {monitor.name}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        Type
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={monitor.monitorType}
                          size="small"
                          sx={{
                            background: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          icon={<CheckCircle />}
                          label={getStatusConfig(monitor.status).label}
                          size="small"
                          sx={{
                            background: alpha(getStatusConfig(monitor.status).color, 0.1),
                            color: getStatusConfig(monitor.status).color,
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {monitor && (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                            Host
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                color: theme.palette.primary.main,
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {
                                monitor.pingMonitor?.host ||
                                monitor.httpMonitor?.host ||
                                monitor.portMonitor?.host ||
                                monitor.keyWordMonitor?.host ||
                                monitor.cronJobMonitor?.host
                              }
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(monitor.pingMonitor?.host ||
                                monitor.httpMonitor?.host ||
                                monitor.portMonitor?.host ||
                                monitor.keyWordMonitor?.host ||
                                monitor.cronJobMonitor?.host)}
                            >
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                        {monitor.httpMonitor?.method ||
                              monitor.portMonitor?.port ||
                              monitor.keyWordMonitor?.method && ( <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                            {monitor.portMonitor ? "Port" : "Method"}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {
                              monitor.httpMonitor?.method ||
                              monitor.portMonitor?.port ||
                              monitor.keyWordMonitor?.method
                            }
                          </Typography>
                        </Box>)}
                       

                        <Box>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                            Interval
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {monitor.cronSchedule}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </GlowCard>

            {/* World Map */}
            <GlowCard
              component={motion.div}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Public sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Monitoring Regions
                  </Typography>
                </Box>
                <WorldMap monitorHost={monitor.name} monitorStatus={monitor.status} monitorType={monitor.monitorType}/>
              </CardContent>
            </GlowCard>

            {/* Response Time Chart */}
            {!monitor.cronJobMonitor && (
              <GlowCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <AccessTime sx={{ color: theme.palette.info.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Response Time History
                    </Typography>
                  </Box>
                  <ResponseTimeLineChart logs={monitor.logs} />
                </CardContent>
              </GlowCard>
            )}

            
          </Stack>
        </Grid>

        {/* Right Column - Stats & Info */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Server Owner Info */}
            {monitor.serverOwner && (
              <GlowCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Person sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Server Owner
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {monitor.serverOwner.name}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {monitor.serverOwner.email}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        Role
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={monitor.serverOwner.role}
                          size="small"
                          color="info"
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={monitor.serverOwner.status ? 'Active' : 'Inactive'}
                          size="small"
                          color={monitor.serverOwner.status ? 'success' : 'error'}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </GlowCard>
            )}
            {/* Recent Logs */}
            <GlowCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Schedule sx={{ color: theme.palette.warning.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Recent Logs
                  </Typography>
                </Box>

                <TableContainer
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.5),
                    borderRadius: 2,
                    maxHeight: 500,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {['Time', 'Status', 'Response Time', 'Error'].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              // Opak zemin: alttaki satırların başlığın arkasından görünmesini engeller
                              // (background.paper temada %80 opak olduğu için tamamen opak default kullanılıyor)
                              backgroundColor: theme.palette.background.default,
                              backgroundImage: `linear-gradient(${alpha(
                                theme.palette.primary.main,
                                0.28
                              )}, ${alpha(theme.palette.primary.main, 0.28)})`,
                              fontWeight: 700,
                              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monitor.logs
                        ?.sort((a, b) => b.id - a.id)
                        .slice(0, 10)
                        .map((log) => (
                          <TableRow
                            key={log.id}
                            sx={{
                              '&:hover': {
                                background: alpha(theme.palette.primary.main, 0.05),
                              },
                            }}
                          >
                            <TableCell>
                              {new Date(log.createdAt).toLocaleString('tr-TR')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={log.status.toUpperCase()}
                                sx={{
                                  background: alpha(getStatusConfig(log.status).color, 0.1),
                                  color: getStatusConfig(log.status).color,
                                  fontWeight: 700,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  color: theme.palette.info.main,
                                  fontWeight: 600,
                                }}
                              >
                                {log.responseTime}ms
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={log.isError ? 'Yes' : 'No'}
                                color={log.isError ? 'error' : 'success'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </GlowCard>
            {/* Quick Stats */}
            <GlowCard>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Statistics
                </Typography>

                <Stack spacing={2}>
                  {[
                    { label: 'Min Response', value: `${stats.minResponseTime}ms`, color: 'success' },
                    { label: 'Max Response', value: `${stats.maxResponseTime}ms`, color: 'warning' },
                    { label: 'Avg Response', value: `${stats.avgResponseTime}ms`, color: 'info' },
                    { label: 'Success Rate', value: `${stats.uptime}%`, color: 'primary' },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        background: alpha(theme.palette[item.color].main, 0.05),
                        border: `1px solid ${alpha(theme.palette[item.color].main, 0.2)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                        {item.label}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette[item.color].main,
                          fontWeight: 700,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                  
                </Stack>
              </CardContent>
            </GlowCard>
            
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}